import { z } from "zod";
import { IFile, TApiObjectBase } from "../base";
import { TReadAccess } from "../common/readAccess";
import { TApiRelatives } from "../common/relative";
import {
  TDocumentAIClassification,
  TDocumentAIType,
  TFileDataFields,
  TProcessingState,
} from "./file";

// Define supported file types directly within the file
export const SUPPORTED_DATA_TYPES = [
  "pdf",
  "docx",
  "doc",
  "ppt",
  "pptx",
  "xlsx",
  "xls",
  "xlsm",
  "xlsb",
  "csv",
  "msg",
  "dwg",
  "obj",
  "eml",
  "db",
  "zip",
  "axd",
  "ifc",
  "rvt",
  "smi",
  "htm",
  "html",
  "thmx",
  "xml",
];

export const SUPPORTED_IMAGE_TYPES = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "svg",
  "tiff",
  "heic",
];
export const SUPPORTED_VIDEO_TYPES = ["mp4", "mov"];
export const SUPPORTED_AUDIO_TYPES = ["wav", "mp3"];

export const TApiFileTypes = [
  ...SUPPORTED_DATA_TYPES,
  ...SUPPORTED_IMAGE_TYPES,
  ...SUPPORTED_VIDEO_TYPES,
  ...SUPPORTED_AUDIO_TYPES,
];

export const UploadFilesRequest = z
  .object({
    files: z.array(
      z
        .object({
          name: z.string(),
          file: z.string(),
          propertyIDs: z.array(z.string()).optional(),
          taskPaths: z.array(z.string()).optional(),
        })
        .strict()
    ),
  })
  .strict();

export type IUploadFilesRequest = z.infer<typeof UploadFilesRequest>;

export const RemoveFileReferenceRequest = z
  .object({
    fileId: z.string().min(1, { message: "fileID cannot be empty" }),
    taskPath: z.string().min(1, { message: "taskPath cannot be empty" }),
  })
  .strict();

export type IRemoveFileReferenceRequest = z.infer<
  typeof RemoveFileReferenceRequest
>;

export type UpdatedFile = {
  old: Record<string, IFile> | null;
  new: Record<string, IFile> | null;
};

export type IFileToUpload = z.infer<typeof TFileToUpload>;
export const TFileToUpload = z
  .object({
    name: z.string(),
    file: z.string(),
    propertyIDs: z.array(z.string()).optional(),
    taskPaths: z.array(z.string()).optional(),
  })
  .strict();

export type IFilesToUpload = z.infer<typeof TFilesToUpload>;
export const TFilesToUpload = z
  .object({
    files: z.array(TFileToUpload),
  })
  .strict();

export const UpdateFilesRequest = z
  .object({
    files: z.array(
      z
        .object({
          storagePath: z.string(),
          name: z.string().optional(),
          file: z.string().optional(),
          propertyIDs: z.array(z.string()).optional(),
          taskPaths: z.array(z.string()).optional(),
        })
        .strict()
    ),
  })
  .strict();

export type IUpdateFilesRequest = z.infer<typeof UpdateFilesRequest>;

export const TApiFile = z
  .object({
    name: z.string(),
    type: z.string().nullish(),
    storagePath: z.string().nullish(),
    previewPath: z.string().nullish(),
    firestorePath: z.string(),
    size: z.number().nullish(),
    tags: z.array(z.string()),

    propertyIDs: z.array(z.string()),
    readAccess: TReadAccess,
    archived: z.boolean(),
    relatives: TApiRelatives,
    folderIDs: z.array(z.string()),

    // External
    externalURL: z.string().nullish(),
    externalRefs: z
      .record(
        z.object({
          id: z.string().nullish(),
        })
      )
      .nullish(),
    externalFileClientID: z.string().nullish(),
    externalFileMetadata: z
      .object({
        propertyID: z.string().nullish(),
      })
      .nullish(),

    // AI-fields
    senderEmail: z.string().nullish(),
    emailText: z.string().nullish(),
    rawText: z.string().nullish(),

    contentSummary: z.string().nullish(),
    documentType: TDocumentAIType.nullish(),
    selectedClassification: TDocumentAIClassification.nullish(),
    classifications: z.array(TDocumentAIClassification),
    processingState: TProcessingState,
    AIDataFields: TFileDataFields.optional(),
    dataFields: TFileDataFields.optional(),

    // Search
    searchHighlight: z.string().nullish(),
  })
  .extend(TApiObjectBase.shape);
export type IApiFile = z.infer<typeof TApiFile>;
